import React, { useLayoutEffect, useRef } from "react";
import clsx from "clsx";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Refresh from "@material-ui/icons/Refresh";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Breakpoints from "common/breakpoints";
import { DASH } from "common/constants";
import WarningMessageDialog from "pages/HorseDetailsPage/Vet/Header/WarningMessageDialog";
import { getHorseAgeSexColorValue } from "utils/horse";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
  isShowHorseInfoDesktop: boolean;
  isActiveReset: boolean;
  onOpenHorseInfoMobile: () => void;
  onOpenResetModal: () => void;
  warningMessage: racehorse360.IRelevantHorseWarningMessage;
  isPullRelevantHorseWarningMessageFetching: boolean;
  refetchHorseWarningMessage: () => void;
  reasonNote?: string;
  onHeightChange: (height: number) => void;
}

const ExamFormHeader = (props: IProps) => {
  const {
    horse,
    isShowHorseInfoDesktop,
    onOpenHorseInfoMobile,
    onOpenResetModal,
    isActiveReset,
    warningMessage,
    isPullRelevantHorseWarningMessageFetching,
    refetchHorseWarningMessage,
    reasonNote,
    onHeightChange
  } = props;

  const classes = useStyles();
  const headerRef = useRef<HTMLDivElement>(null);
  const matchesDownSM600 = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(Breakpoints.SM_600)
  );

  useLayoutEffect(() => {
    const header = headerRef.current;

    if (!header) return;

    const updateHeight = () => {
      onHeightChange(Math.ceil(header.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(header);

    return () => resizeObserver.disconnect();
  }, [onHeightChange]);

  const renderRightExamHeaderSide = () => {
    return matchesDownSM600 ? (
      <IconButton
        className={classes.infoButton}
        onClick={onOpenHorseInfoMobile}
      >
        <InfoOutlinedIcon />
      </IconButton>
    ) : (
      <Button
        color="primary"
        className={classes.resetButton}
        startIcon={<Refresh />}
        onClick={onOpenResetModal}
        disabled={!isActiveReset}
        data-test-id={"resetExamButton"}
      >
        Reset Exam
      </Button>
    );
  };

  return (
    <div
      ref={headerRef}
      className={clsx(classes.header, classes.examFormHeader, {
        ["hideRightPanel"]: isShowHorseInfoDesktop
      })}
    >
      <div className={classes.leftSide}>
        {Boolean(horse) && (
          <WarningMessageDialog
            horse={horse}
            warningMessage={warningMessage}
            isPullRelevantHorseWarningMessageFetching={
              isPullRelevantHorseWarningMessageFetching
            }
            refetchHorseWarningMessage={refetchHorseWarningMessage}
          />
        )}
        <div className={classes.horseData}>
          <div className={classes.primaryHorseInfo}>
            {horse?.name || "Horse Name"}
          </div>
          <div className={classes.secondaryHorseInfo}>
            Chip ID # {horse?.chipNumber || DASH}
          </div>
          <div className={classes.secondaryHorseInfo}>
            C.S.A - {getHorseAgeSexColorValue(horse, undefined, DASH)}
          </div>
          {Boolean(reasonNote) && (
            <div className={classes.secondaryHorseInfo}>{reasonNote}</div>
          )}
        </div>
      </div>
      {renderRightExamHeaderSide()}
    </div>
  );
};

export default ExamFormHeader;
